import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  WarningIcon,
  InfoIcon,
  WarningCircleIcon,
  XCircleIcon,
  CheckCircleIcon,
  TrashIcon,
  DownloadIcon,
} from '@phosphor-icons/react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DeltaAnalysisResult } from '@erp/shared-types';

interface DeltaWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  analysis: DeltaAnalysisResult | null;
  isLoading?: boolean;
  invoices?: Array<{
    id: string;
    files?: {
      transformedFile?: { id: string; name: string } | null;
    };
  }>;
  onDownloadDocument?: (documentId: string, fileName?: string) => void;
}

const SeverityIcon = ({ severity }: { severity: string }) => {
  switch (severity) {
    case 'destructive':
      return <XCircleIcon className="h-5 w-5 text-red-600" />;
    case 'major':
      return <WarningCircleIcon className="h-5 w-5 text-orange-600" />;
    case 'minor':
      return <InfoIcon className="h-5 w-5 text-blue-600" />;
    default:
      return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
  }
};

const SeverityBadge = ({ severity }: { severity: string }) => {
  const variants: Record<string, 'destructive' | 'default' | 'secondary'> = {
    destructive: 'destructive',
    major: 'default',
    minor: 'secondary',
    none: 'secondary',
  };

  return (
    <Badge variant={variants[severity]} className="ml-2">
      {severity.toUpperCase()}
    </Badge>
  );
};

export const DeltaWarningDialog: React.FC<DeltaWarningDialogProps> = ({
  open,
  onClose,
  onConfirm,
  analysis,
  isLoading,
  invoices,
  onDownloadDocument,
}) => {
  if (!analysis) return null;

  // Helper function to get transformed file for an invoice
  const getTransformedFile = (invoiceId: string) => {
    if (!invoices) return null;
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    return invoice?.files?.transformedFile || null;
  };

  const {
    severity,
    activeProductGroupsDelta,
    invoiceImpact,
    paymentImpact,
    manualAmountImpact,
    exclusiveRuleWarning,
  } = analysis;

  const hasRemovedGroups = activeProductGroupsDelta.removed.length > 0;
  const hasAddedGroups = activeProductGroupsDelta.added.length > 0;
  const hasExclusiveRuleWarning = exclusiveRuleWarning?.hasExclusiveRules;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-[90vw] sm:!max-w-[90vw] !w-[90vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <SeverityIcon severity={severity} />
            <span className="ml-2">Review Agreement Changes</span>
            <SeverityBadge severity={severity} />
          </DialogTitle>
          <DialogDescription>
            {severity === 'destructive' &&
              'These changes will affect existing vendor reported amounts and payments. Please review the impacts carefully before proceeding.'}
            {severity === 'major' &&
              'These changes will impact existing payment allocations and rebate calculations. Review the details below.'}
            {severity === 'minor' &&
              'These changes have minimal impact. You can proceed with confidence.'}
            {severity === 'none' &&
              'These changes are safe and will not affect any existing data.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Product Groups Delta */}
          {(hasRemovedGroups || hasAddedGroups) && (
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-3">
                Product Coverage Changes
              </h3>
              <div className="space-y-2">
                {hasRemovedGroups && (
                  <AlertContainer variant="destructive">
                    <WarningIcon className="h-4 w-4" />
                    <AlertTitle>
                      Products Being Removed from Agreement
                    </AlertTitle>
                    <AlertDescription>
                      <div className="mt-2 text-sm mb-2">
                        The following product groups will no longer be covered
                        by this agreement:
                      </div>
                      <div className="mt-2 space-y-1">
                        {activeProductGroupsDelta.removed.map((group) => (
                          <div key={group} className="font-mono text-sm">
                            • {group}
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </AlertContainer>
                )}
                {hasAddedGroups && (
                  <AlertContainer>
                    <InfoIcon className="h-4 w-4" />
                    <AlertTitle>Products Being Added to Agreement</AlertTitle>
                    <AlertDescription>
                      <div className="mt-2 text-sm mb-2">
                        The following product groups will now be covered by this
                        agreement:
                      </div>
                      <div className="mt-2 space-y-1">
                        {activeProductGroupsDelta.added.map((group) => (
                          <div key={group} className="font-mono text-sm">
                            • {group}
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </AlertContainer>
                )}
              </div>
            </Card>
          )}

          {/* Exclusive Rule Warning */}
          {hasExclusiveRuleWarning && (
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-3">
                ⚠️ Exclusive Rules Impact
              </h3>
              <AlertContainer
                variant={
                  invoiceImpact.totalInvoices > 0 ? 'destructive' : 'default'
                }
              >
                <WarningIcon className="h-4 w-4" />
                <AlertTitle>
                  Changes May Affect Exclusive Rule Calculations
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    Adding new product groups may change which products are
                    included or excluded in your exclusive rules. This could
                    affect rebate calculations.
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="font-semibold text-sm">
                      Products Being Added:
                    </div>
                    <div className="space-y-1">
                      {exclusiveRuleWarning.addedProductGroups.map((group) => (
                        <div key={group} className="font-mono text-sm ml-4">
                          • {group}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="font-semibold text-sm">
                      Rules That May Be Affected:
                    </div>
                    {exclusiveRuleWarning.affectedRules.map((rule) => (
                      <div
                        key={rule.ruleName}
                        className="p-3 bg-orange-50 rounded text-sm border border-orange-200"
                      >
                        <div className="font-semibold">{rule.ruleName}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {rule.qualifyingIsExclusive &&
                            'Has exclusive qualifying products • '}
                          {rule.affectedIsExclusive &&
                            'Has exclusive rebate products • '}
                          Currently excluding:{' '}
                          {rule.currentExcludedGroups.length > 0
                            ? rule.currentExcludedGroups.join(', ')
                            : 'None'}
                        </div>
                      </div>
                    ))}
                  </div>
                  {invoiceImpact.totalInvoices > 0 && (
                    <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                      <div className="text-sm font-semibold text-red-900">
                        ⚠️ Historical Data Warning
                      </div>
                      <div className="text-sm text-red-800 mt-1">
                        {invoiceImpact.totalInvoices} existing vendor reported
                        amounts
                        {invoiceImpact.totalInvoices > 1 ? 's' : ''} may
                        calculate different rebate amounts after these changes
                        due to the expanded product scope.
                      </div>
                    </div>
                  )}
                </AlertDescription>
              </AlertContainer>
            </Card>
          )}

          {/* Section 1: CSV/Invoice Impact */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">
              Impact on Existing Vendor Reported Amounts
            </h3>
            {invoiceImpact.incompatibleInvoices > 0 ? (
              <AlertContainer variant="destructive">
                <XCircleIcon className="h-4 w-4" />
                <AlertTitle>
                  {invoiceImpact.incompatibleInvoices} Vendor Reported Amount
                  {invoiceImpact.incompatibleInvoices > 1 ? 's' : ''} Will No
                  Longer Process
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    These vendor reported amounts contain products that are
                    being removed from the agreement. They will not be able to
                    earn rebates after this change, and be deleted.
                  </div>
                  <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                    {invoiceImpact.incompatibleDetails.map((detail) => {
                      const transformedFile = getTransformedFile(
                        detail.invoiceId,
                      );
                      return (
                        <div
                          key={detail.invoiceId}
                          className="p-3 bg-red-50 rounded text-sm border border-red-200 flex items-start gap-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold">
                              Vendor Reported Amount from{' '}
                              {new Date(
                                detail.invoiceDate,
                              ).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {detail.affectedLineItems} line item
                              {detail.affectedLineItems > 1 ? 's' : ''} will no
                              longer qualify • Products being removed:{' '}
                              {detail.missingProductGroups.join(', ')}
                            </div>
                          </div>
                          {transformedFile && onDownloadDocument && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 flex-shrink-0"
                              onClick={() =>
                                onDownloadDocument(
                                  transformedFile.id,
                                  transformedFile.name,
                                )
                              }
                            >
                              <DownloadIcon className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </AlertDescription>
              </AlertContainer>
            ) : (
              <AlertContainer>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <AlertTitle>All Vendor Reported Amounts Compatible</AlertTitle>
                <AlertDescription>
                  {invoiceImpact.totalInvoices === 0
                    ? 'No existing vendor reported amounts to check.'
                    : `All ${invoiceImpact.totalInvoices} existing vendor reported amount${invoiceImpact.totalInvoices > 1 ? 's' : ''} will continue to work with these changes.`}
                </AlertDescription>
              </AlertContainer>
            )}
          </Card>

          {/* Section 2: Vendor Payment Allocations */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">
              Impact on Vendor Payment Allocations
            </h3>
            {paymentImpact.affectedAllocations > 0 ? (
              <AlertContainer variant="destructive">
                <WarningCircleIcon className="h-4 w-4" />
                <AlertTitle>Payment Allocations Will Be Removed</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    You are deleting{' '}
                    {paymentImpact.deletedRules.length > 1 ? 'rules' : 'rule'}{' '}
                    <span className="font-semibold">
                      {paymentImpact.deletedRules.join(', ')}
                    </span>
                    . This will remove {paymentImpact.affectedAllocations}{' '}
                    payment allocation
                    {paymentImpact.affectedAllocations > 1
                      ? 's'
                      : ''} totaling{' '}
                    <span className="font-semibold">
                      ${paymentImpact.affectedAmount.toLocaleString()}
                    </span>{' '}
                    that{' '}
                    {paymentImpact.affectedAllocations > 1 ? 'were' : 'was'}{' '}
                    allocated to{' '}
                    {paymentImpact.deletedRules.length > 1
                      ? 'these rules'
                      : 'this rule'}
                    .
                  </div>
                  {paymentImpact.unallocatableDetails.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                      {paymentImpact.unallocatableDetails.map((detail) => (
                        <div
                          key={detail.paymentId}
                          className="p-3 bg-red-50 rounded text-sm border border-red-200 flex items-start gap-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold mb-1">
                              Payment: ${detail.paymentAmount.toLocaleString()}{' '}
                              on{' '}
                              {new Date(
                                detail.paymentDate,
                              ).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              ${detail.allocationAmount.toLocaleString()}{' '}
                              allocated to {detail.affectedRules.join(', ')}{' '}
                              will be removed
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AlertDescription>
              </AlertContainer>
            ) : (
              <AlertContainer>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <AlertTitle>No Payment Impact</AlertTitle>
                <AlertDescription>
                  {paymentImpact.totalPayments === 0
                    ? 'No vendor payments to check.'
                    : `All ${paymentImpact.totalPayments} vendor payment${paymentImpact.totalPayments > 1 ? 's' : ''} will remain intact.`}
                </AlertDescription>
              </AlertContainer>
            )}
          </Card>

          {/* Section 3: Manual Rebate Rule Payments */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">
              Impact on Manually Recorded Rebates
            </h3>
            {manualAmountImpact.affectedManualAmounts > 0 ? (
              <AlertContainer variant="destructive">
                <WarningCircleIcon className="h-4 w-4" />
                <AlertTitle>
                  Manually Recorded Rebates Will Be Removed
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    You are deleting{' '}
                    {manualAmountImpact.deletedRules.length > 1
                      ? 'rules'
                      : 'rule'}{' '}
                    <span className="font-semibold">
                      {manualAmountImpact.deletedRules.join(', ')}
                    </span>
                    . This will remove{' '}
                    {manualAmountImpact.affectedManualAmounts} manually recorded
                    rebate(s)
                    {manualAmountImpact.affectedManualAmounts > 1
                      ? 's'
                      : ''}{' '}
                    totaling{' '}
                    <span className="font-semibold">
                      ${manualAmountImpact.affectedTotal.toLocaleString()}
                    </span>{' '}
                    that{' '}
                    {manualAmountImpact.affectedManualAmounts > 1
                      ? 'were'
                      : 'was'}{' '}
                    tied to{' '}
                    {manualAmountImpact.deletedRules.length > 1
                      ? 'these rules'
                      : 'this rule'}
                    .
                  </div>
                  {manualAmountImpact.manualAmountDetails.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                      {manualAmountImpact.manualAmountDetails.map((detail) => (
                        <div
                          key={detail.manualAmountId}
                          className="p-3 bg-red-50 rounded text-sm border border-red-200 flex items-start gap-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold mb-1">
                              Manually Recorded Rebate: $
                              {detail.amount.toLocaleString()} on{' '}
                              {new Date(detail.date).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Division: {detail.divisionName} • Rule:{' '}
                              {detail.ruleName}
                              {detail.description && (
                                <span> • Note: {detail.description}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AlertDescription>
              </AlertContainer>
            ) : (
              <AlertContainer>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <AlertTitle>No Manually Recorded Rebate Impact</AlertTitle>
                <AlertDescription>
                  {manualAmountImpact.totalManualAmounts === 0
                    ? 'No manually recorded rebates to check.'
                    : `All ${manualAmountImpact.totalManualAmounts} manually recorded rebate${manualAmountImpact.totalManualAmounts > 1 ? 's' : ''} will remain intact.`}
                </AlertDescription>
              </AlertContainer>
            )}
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Go Back
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            variant={severity === 'destructive' ? 'destructive' : 'default'}
          >
            {isLoading
              ? 'Saving Changes...'
              : severity === 'destructive'
                ? 'I Understand - Save Changes'
                : 'Confirm & Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
